/**
 * useSortable — tiny click-to-sort hook for tables.
 *
 * Adapted from the TNSC reference — kept small and deps-free so it can be
 * reused on any future table (loan / deposit drill-ins, etc.). NULL /
 * undefined values always sink to the bottom regardless of direction.
 *
 * First click on a column sorts DESCENDING (best/newest first), second
 * click flips to ASC, third flips back to DESC.
 */

import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

export type SortAccessor<T> = (row: T) => number | string | null | undefined;

export interface SortState {
	sortBy: string | null;
	sortDir: SortDir;
}

export interface UseSortableResult<T> {
	rows: T[];
	sortBy: string | null;
	sortDir: SortDir;
	toggle: (field: string) => void;
}

export function useSortable<T>(
	rows: T[],
	initial?: { field: string; dir?: SortDir },
	accessors?: Record<string, SortAccessor<T>>,
): UseSortableResult<T> {
	const [state, setState] = useState<SortState>({
		sortBy: initial?.field ?? null,
		sortDir: initial?.dir ?? 'desc',
	});

	const toggle = (field: string) => {
		setState((prev) => {
			if (prev.sortBy === field) {
				return { sortBy: field, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' };
			}
			return { sortBy: field, sortDir: 'desc' };
		});
	};

	const sorted = useMemo(() => {
		if (!state.sortBy) return rows;
		const key = state.sortBy;
		const accessor: SortAccessor<T> =
			accessors?.[key] ??
			((r: T) =>
				(r as unknown as Record<string, unknown>)[key] as
					| number
					| string
					| null
					| undefined);
		const mult = state.sortDir === 'asc' ? 1 : -1;
		const copy = [...rows];
		copy.sort((a, b) => {
			const av = accessor(a);
			const bv = accessor(b);
			const aMissing = av === null || av === undefined;
			const bMissing = bv === null || bv === undefined;
			if (aMissing && bMissing) return 0;
			if (aMissing) return 1;
			if (bMissing) return -1;
			if (typeof av === 'number' && typeof bv === 'number') {
				return (av - bv) * mult;
			}
			return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mult;
		});
		return copy;
	}, [rows, state, accessors]);

	return { rows: sorted, sortBy: state.sortBy, sortDir: state.sortDir, toggle };
}
